import { fileUrl } from "../../lib/api";
import { useConfigEditor, SaveBar, Field, ImageInput, Section } from "../../lib/admin-shared";
import { ImageIcon } from "lucide-react";

export default function IdentityEditor() {
    const { cfg, set, save, saving, saved, loading } = useConfigEditor();
    if (loading || !cfg) return <div className="text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-3xl space-y-8" data-testid="identity-editor">
            <div>
                <h1 className="font-display text-4xl mb-2">Site Identity</h1>
                <p className="text-muted-foreground">Your organization name, tagline, logo, and SEO metadata.</p>
            </div>

            <Section title="Brand">
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Site name" value={cfg.site_name} onChange={(v) => set("site_name", v)} testid="id-site-name" />
                    <Field label="Tagline" value={cfg.tagline} onChange={(v) => set("tagline", v)} testid="id-tagline" />
                </div>
                <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Logo</span>
                    <div className="flex items-start gap-4">
                        {cfg.logo_url ? (
                            <img src={fileUrl(cfg.logo_url)} alt="Logo" className="h-24 w-24 object-contain border border-border rounded-sm bg-background p-2" data-testid="id-logo-preview" />
                        ) : (
                            <div className="h-24 w-24 flex items-center justify-center border border-border rounded-sm text-muted-foreground"><ImageIcon size={22} /></div>
                        )}
                        <div className="flex-1">
                            <ImageInput value={cfg.logo_url} onChange={(v) => set("logo_url", v)} testid="id-logo" />
                            {cfg.logo_url && (
                                <button type="button" onClick={() => set("logo_url", "")} className="mt-2 text-xs text-muted-foreground hover:text-destructive" data-testid="id-logo-clear">Clear logo</button>
                            )}
                        </div>
                    </div>
                </div>

                <div>
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Site icon (browser tab / favicon)</span>
                    <div className="flex items-start gap-4">
                        {cfg.favicon_url ? (
                            <img src={fileUrl(cfg.favicon_url)} alt="Site icon" className="h-16 w-16 object-contain border border-border rounded-sm bg-background p-1" data-testid="id-favicon-preview" />
                        ) : (
                            <div className="h-16 w-16 flex items-center justify-center border border-border rounded-sm text-muted-foreground"><ImageIcon size={18} /></div>
                        )}
                        <div className="flex-1">
                            <ImageInput value={cfg.favicon_url} onChange={(v) => set("favicon_url", v)} testid="id-favicon" />
                            <p className="text-xs text-muted-foreground mt-2">Square image, ideally 512×512 PNG or JPG. Shows in the browser tab and when the site is bookmarked.</p>
                            {cfg.favicon_url && (
                                <button type="button" onClick={() => set("favicon_url", "")} className="mt-1 text-xs text-muted-foreground hover:text-destructive" data-testid="id-favicon-clear">Reset to default</button>
                            )}
                        </div>
                    </div>
                </div>
            </Section>

            <Section title="SEO" description="What search engines and social shares display.">
                <Field label="Page title (browser tab)" value={cfg.seo_title} onChange={(v) => set("seo_title", v)} testid="id-seo-title" />
                <label className="block">
                    <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2 block">Meta description (150–160 chars)</span>
                    <textarea rows={3} value={cfg.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} maxLength={300}
                        className="w-full px-4 py-3 rounded-sm border border-border bg-background text-sm" data-testid="id-seo-desc" />
                    <div className="text-xs text-muted-foreground mt-1 text-right">{(cfg.seo_description || "").length} chars</div>
                </label>
                <Field label="Keywords (comma-separated)" value={cfg.seo_keywords} onChange={(v) => set("seo_keywords", v)} testid="id-seo-keywords" />
            </Section>

            <Section title="Email sender" description="The 'From' address for donation receipts. Requires verified domain in Resend.">
                <div className="grid sm:grid-cols-2 gap-5">
                    <Field label="Sender email" value={cfg.sender_email} onChange={(v) => set("sender_email", v)} testid="id-sender-email" />
                    <Field label="Sender name" value={cfg.sender_name} onChange={(v) => set("sender_name", v)} testid="id-sender-name" />
                </div>
            </Section>

            <SaveBar onSave={save} saving={saving} saved={saved} />
        </div>
    );
}
